import "./BasicInformationAddComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import React, {useCallback, useEffect, useRef, useState} from "react";
import * as Yup from "yup";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import CommonService from "../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import Grid from "@mui/material/Grid";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormSubTitleWithTooltipComponent
    from "../../../../shared/components/form-sub-title-with-tooltip/FormSubTitleWithTooltipComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import {AttachFile} from "@mui/icons-material";
import commonService from "../../../../shared/services/common.service";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";

interface BasicInformationAddComponentProps {
    next: Function;
    cancel: Function;
    productId?: any;
    productDetails?: any;
}

const productInformationValidationSchema = Yup.object({
        name: Yup.string()
            .required("Product Name is required"),
        panelId: Yup.mixed().required("Panel Name is required"),
        productCode: Yup.string().required("Product Code is required"),
        quantity: Yup.string()
            .required("Available Quantity is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false),
        alertLevelStock: Yup.string().required("Alert Level Quantity is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false),
        price: Yup.string().required("Retail Price is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false),
        description: Yup.string().required("Description is required"),
    }
);


interface productInformationFormSchema {
    name: string;
    panelId: string;
    productCode: string;
    quantity: string;
    alertLevelStock: string;
    price: string;
    description: string;
}

const productInformationInitialValues: productInformationFormSchema = {
    name: "",
    panelId: "",
    productCode: "",
    quantity: "",
    alertLevelStock: "",
    price: "",
    description: "",
}

const BasicInformationAddComponent = (props: BasicInformationAddComponentProps) => {
    const {next, cancel, productDetails, productId} = props;
    const [productDetailsInitialValues, setProductDetailsInitialValues] = useState<productInformationFormSchema>(productInformationInitialValues);
    const productImagesFileRef = useRef<HTMLInputElement | null>(null);
    const [productImages, setProductImages] = useState<any[]>([]);

    const {
        testPanelList
    } = useSelector((state: IRootReducerState) => state.staticData);

    const handleTemplateAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = null;
            event.target.files = null;
            return;
        }
        if (productId) {
            const formData = new FormData();
            formData.append('image', file);
            commonService._inventory.addProductImage(productId, formData)
                .then((response: IAPIResponseType<any>) => {
                    setProductImages((prevState: any[]) => {
                        return [...prevState, file];
                    })
                })
                .catch((error: any) => {
                    commonService._alert.showToast(error.message, "error");
                })
        } else {
            setProductImages((prevState: any[]) => {
                return [...prevState, file];
            })
        }
        event.target.value = null;
        event.target.files = null;
    }, [productId]);

    const handleProductImageRemove = useCallback((index: number) => {
        setProductImages((prevState: any[]) => {
            const newState = [...prevState];
            newState.splice(index, 1);
            return newState;
        })
    }, []);

    const handleProductAddSuccess = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        const payload = {...values};
        payload.panelId = payload.panelId?.id;
        payload.images = productImages;
        const formData = commonService.getFormDataFromJSON(payload);
        setSubmitting(true);
        if (!productId) {
            CommonService._inventory.addProduct(formData)
                .then((response: IAPIResponseType<any>) => {
                    setSubmitting(false);
                    next(response.data.id);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        } else {
            commonService._inventory.editProduct(productId, formData)
                .then((response: IAPIResponseType<any>) => {
                    setSubmitting(false);
                    next(response.data.id);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }
    }, [next, productImages, productId]);

    useEffect(() => {
        if (productDetails) {
            setProductDetailsInitialValues({
                ...productDetails,
                panelId: testPanelList.find((item: any) => item.id === productDetails.panelId),
            })
            setProductImages(productDetails.images)
        }
    }, [productDetails, testPanelList]);


    return (
        <div className={'basic-information-add-component t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Basic Information'}
                tooltipData={"Ensure that the name of the Facility is unique. By entering a ZIP Code, the state and city will be auto populated."}
            />
            <div className="t-form">
                <Formik
                    validationSchema={productInformationValidationSchema}
                    initialValues={productDetailsInitialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={handleProductAddSuccess}
                >
                    {({isSubmitting, values, isValid, setFieldValue, validateForm, errors}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            // eslint-disable-next-line no-script-url
                            <Form noValidate={true} action={'javascript:void'}>
                                <div className={"t-form-controls-wrapper"}>
                                    <Field name={'name'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Product Name'}
                                                    placeholder={'Enter Product Name'}
                                                    type={"text"}
                                                    required={true}
                                                    formikField={field}
                                                />
                                            )}
                                    </Field>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'panelId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Panel Name'}
                                                            placeholder={'Select Panel Name'}
                                                            data={testPanelList}
                                                            multiple={false}
                                                            valueKey={"id"}
                                                            getOptionLabel={(option => (option.name) || '')}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'productCode'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            fullWidth={true}
                                                            label={'Product Code'}
                                                            required={true}
                                                            placeholder={'Enter Product Code'}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'quantity'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            fullWidth={true}
                                                            label={'Available Quantity'}
                                                            required={true}
                                                            placeholder={'Enter Available Quantity'}
                                                            formikField={field}
                                                        />
                                                    )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'alertLevelStock'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            fullWidth={true}
                                                            label={'Alert Level Quantity'}
                                                            required={true}
                                                            placeholder={'Enter Alert Level Quantity'}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'price'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            fullWidth={true}
                                                            label={'Retail Price'}
                                                            required={true}
                                                            placeholder={'Enter Retail Price'}
                                                            formikField={field}
                                                            prefix={'$'}
                                                        />
                                                    )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                        </Grid>
                                    </Grid>
                                    <Field name={'description'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    fullWidth={true}
                                                    label={'Description'}
                                                    multiline={true}
                                                    rows={3}
                                                    required={true}
                                                    placeholder={'Enter Description'}
                                                    formikField={field}
                                                />
                                            )}
                                    </Field>
                                    <div className={'horizontal-line'}>
                                    </div>
                                    <FormSubTitleWithTooltipComponent title={"Product Images*"}
                                                                      tooltipData={"Product Images"}/>
                                    <input type={"file"}
                                           className={"display-none"}
                                           ref={productImagesFileRef}
                                           accept={"image/jpeg,image/gif,image/png"}
                                           onChange={(event) => {
                                               handleTemplateAttachmentUpload(event);
                                           }}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={6}>
                                            <ButtonComponent
                                                variant={"outlined"}
                                                fullWidth={false}
                                                color={'secondary'}
                                                handleClick={() => {
                                                    productImagesFileRef.current?.click()
                                                }}
                                                startIcon={<FileUploadIcon/>}
                                                type={'button'}>
                                                Upload
                                            </ButtonComponent>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={6}>
                                        </Grid>
                                    </Grid>
                                    <div className={'product-images-wrapper mrg-top-20'}>
                                        {
                                            productImages.map((image: any, index: number) => {
                                                return (
                                                    <div key={index} className={'mrg-bottom-20'}>
                                                        <ChipComponent
                                                            label={'product_image'}
                                                            onDelete={() => {
                                                                handleProductImageRemove(index);
                                                            }}
                                                            icon={<AttachFile/>}
                                                        />
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Cancel
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid || isSubmitting || productImages.length === 0}
                                        isLoading={isSubmitting}
                                    >
                                        Proceed
                                    </ButtonComponent>
                                </div>

                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    )
        ;

};

export default BasicInformationAddComponent;
